/** @typedef {import('../index.js').BaseDocData} BaseDocData */
/* eslint-disable jsdoc/no-undefined-types */
import * as Yup from 'yup';
import {
  ReorderSuggestionCancellationReasons,
  ReorderSuggestionStatus,
} from '../../constants/index.js';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

const reorderSuggestionStatusValues = Object.values(ReorderSuggestionStatus);
const cancellationReasonValues = Object.values(ReorderSuggestionCancellationReasons);

const snapshotSchema = Yup.object().shape({
  onHand: Yup.number().integer().min(0).default(0)
    .required('snapshot.onHand is required'),
  incomingSupply: Yup.number().integer().min(0).default(0)
    .required('snapshot.incomingSupply is required'),
  committedDemand: Yup.number().integer().min(0).default(0)
    .required('snapshot.committedDemand is required'),
  netPosition: Yup.number().integer().default(0).required('snapshot.netPosition is required'),
});

export const reorderSuggestionSchema = Yup.object().shape({
  productDocId: Yup.string().required('productDocId is required'),
  productVersionId: Yup.string().required('productVersionId is required'),
  sku: Yup.string().required('sku is required'),
  status: Yup.string().oneOf(reorderSuggestionStatusValues).default(ReorderSuggestionStatus.OPEN)
    .required('status is required'),
  triggerQuantity: Yup.number().integer().min(0).required('triggerQuantity is required'),
  targetLevel: Yup.number().integer().min(0).required('targetLevel is required'),
  snapshot: snapshotSchema.required('snapshot is required'),
  suggestedQuantity: Yup.number().integer().min(0).required('suggestedQuantity is required'),
  internalLineItemId: Yup.string().nullable().default(null),
  createdAt: YupFirestoreTimestamp.nullable(),
  updatedAt: YupFirestoreTimestamp.nullable(),
  fulfilledAt: YupFirestoreTimestamp.nullable(),
  dismissedAt: YupFirestoreTimestamp.nullable(),
  dismissedBy: Yup.object().shape({
    uid: Yup.string().required('dismissedBy.uid is required'),
    email: Yup.string().email().required('dismissedBy.email is required'),
  }).nullable().default(null),
  cancellationReason: Yup.string().nullable().oneOf([...cancellationReasonValues, null]).default(null),
});

/**
 * @typedef {Yup.InferType<typeof reorderSuggestionSchema>} ReorderSuggestionSchema
 * @typedef {BaseDocData & ReorderSuggestionSchema} ReorderSuggestionDocData
 */

export default class ReorderSuggestion {
  /** @type {ReorderSuggestionDocData['validation']} */ validation = 'passed';

  constructor({
    id,
    productDocId,
    productVersionId,
    sku,
    status = ReorderSuggestionStatus.OPEN,
    triggerQuantity,
    targetLevel,
    snapshot,
    suggestedQuantity,
    internalLineItemId = null,
    createdAt,
    updatedAt,
    fulfilledAt = null,
    dismissedAt = null,
    dismissedBy = null,
    cancellationReason = null,
  }) {
    const validatedData = reorderSuggestionSchema.validateSync({
      productDocId,
      productVersionId,
      sku,
      status,
      triggerQuantity,
      targetLevel,
      snapshot,
      suggestedQuantity,
      internalLineItemId,
      createdAt,
      updatedAt,
      fulfilledAt,
      dismissedAt,
      dismissedBy,
      cancellationReason,
    });

    this.id = id || null;
    Object.assign(this, validatedData);
  }

  toFirestore({ serverTimestamp }) {
    return {
      productDocId: this.productDocId,
      productVersionId: this.productVersionId,
      sku: this.sku,
      status: this.status,
      triggerQuantity: this.triggerQuantity,
      targetLevel: this.targetLevel,
      snapshot: this.snapshot,
      suggestedQuantity: this.suggestedQuantity,
      internalLineItemId: this.internalLineItemId,
      createdAt: this.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
      fulfilledAt: this.fulfilledAt,
      dismissedAt: this.dismissedAt,
      dismissedBy: this.dismissedBy,
      cancellationReason: this.cancellationReason,
    };
  }

  static fromFirestore(docData) {
    return new ReorderSuggestion(docData);
  }
}
